import { ClientTable } from "../../../components/ui/ClientTable";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Ban, Check, Info, RefreshCw, Undo2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, LoadingState, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import {
  CLAIM_CATEGORY,
  CLAIM_CATEGORY_LABEL_KEY,
  CLAIM_STATUS,
  TRIP_REASON_LABEL_KEY,
  VEHICLE_TYPE_LABEL_KEY,
  abbreviationFor,
  claimApprovedOn,
  claimSubmitterName,
  lateSubmissionReason,
  formatAmount,
  formatClaimPeriod,
  formatDate,
  formatStamp,
} from "../constants/claimConstants";
import { MODE_LABEL_KEY } from "../constants/conveyanceConstants";
import {
  ALL_CLAIMS_PATH,
  APPROVER_ACTION,
  DETAIL_ORIGIN,
  TEAM_CLAIMS_PATH,
} from "../constants/approvalConstants";
import {
  fetchClaimForApproval,
  fetchClaimLines,
  resolveClaimActions,
  submitApproverAction,
  conveyanceClaimService,
} from "../../../services/conveyanceClaimService";
import { storedProof } from "../lib/claimRules";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { ClaimAuditTrail } from "../components/ClaimAuditTrail";
import { ApproverActionModal } from "../components/ApproverActionModal";
import { ProofCell, ProofPreviewPanel } from "../components/ClaimProofViewer";
import { Toast } from "../components/Toast";
import {
  ApproverDeniedScreen,
  useApproverIdentity,
} from "../components/approverAccess";
import { useClaimCurrencies } from "../components/PolicyGate";

/** One label/value pair in the header block. */
function HeaderField({ label, children }) {
  return (
    <div>
      <span className={ui.sectionLabel}>{label}</span>
      <div className="mt-1 text-sm font-semibold text-foreground">{children ?? "—"}</div>
    </div>
  );
}

const cellClass = "px-3 py-2.5 text-[13px] per-file-hint align-top";
const headCellClass = "px-3 py-2.5 font-semibold whitespace-nowrap";

/**
 * Screen 6 — Claim Detail / Approval View (Team HRMS).
 *
 * An approver opens one claim, reads everything about it, and takes exactly
 * one of three actions. Two properties hold everywhere on this page:
 *
 *   Read-only, always. Nothing here edits a claim. There is no input on any
 *   trip line, no override on an amount, no way to change a date. An approver
 *   who disagrees with a figure has one path — Send Back with a reason — and
 *   the submitter corrects it.
 *
 *   Whole-claim decisions only. There is no per-line approve, no partial
 *   state; the approval unit is the claim.
 *
 * The self-approval guardrail is enforced here as a hard block rather than a
 * warning: if the viewer is the claim's own submitter the action bar is
 * replaced by a stated reason, whatever role they hold. That is the backstop
 * for the case where routing was bypassed by an Org Chart change made after
 * submission.
 *
 * Both approver roles (Manager, Finance_Manager) reach this screen; which of
 * them may act on a given claim is the approval chain's call, resolved by the
 * API, not by this page.
 */
export function ClaimApprovalDetailPage() {
  const { t } = useTranslation();
  const { claimId } = useParams();
  const [searchParams] = useSearchParams();
  const { isApprover, isFinanceManager, viewerEmpId } = useApproverIdentity();
  const currencies = useClaimCurrencies();

  const [claim, setClaim] = useState(null);
  const [lines, setLines] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);

  const [openAction, setOpenAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [previewProof, setPreviewProof] = useState(null);
  const [toast, setToast] = useState(null);

  /** Where the back link returns to — the list that opened this claim. */
  const backTo =
    searchParams.get("from") === DETAIL_ORIGIN.ALL ? ALL_CLAIMS_PATH : TEAM_CLAIMS_PATH;

  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");
    Promise.all([fetchClaimForApproval(claimId), fetchClaimLines(claimId)])
      .then(([loadedClaim, loadedLines]) => {
        setClaim(loadedClaim);
        setLines(loadedLines);
        // The claim carries its own trail; the action endpoint is asked only
        // if it did not and the status says there must be one.
        resolveClaimActions(loadedClaim).then(setActions);

        // Every proof on this screen comes off the claim's attachments — there
        // is no proof endpoint. An entry with a claimLineId belongs to that
        // trip line; one without belongs to the claim as a whole.
        const extractedProofs = (loadedClaim?.attachments || [])
          .map((attachment, index) => {
            const lineIndex = loadedLines.findIndex(
              (line) => String(line.claimLineId) === String(attachment.claimLineId)
            );
            return storedProof(attachment, {
              proofId: `proof-${loadedClaim?.claimId}-${index}`,
              fileNameBase:
                attachment.claimLineId == null
                  ? t("conveyanceApproval.proof")
                  : t("conveyanceClaim.tripLineNo", { number: lineIndex + 1 }),
            });
          })
          .filter(Boolean);

        setProofs(extractedProofs);
      })
      .catch((error) => {
        console.error("Failed to load claim for approval:", error);
        setLoadError(error.message || t("conveyanceApproval.errorLoadClaim"));
      })
      .finally(() => setLoading(false));
  }, [claimId, t]);

  useEffect(() => {
    if (lines.length > 0 && lines.some((l) => l.customerId)) {
      conveyanceClaimService.fetchCustomers().then(setCustomers).catch(console.error);
      const uniqueCustomerIds = [...new Set(lines.filter((l) => l.customerId).map((l) => String(l.customerId)))];
      Promise.all(uniqueCustomerIds.map((id) => conveyanceClaimService.fetchCustomerLocations(id)))
        .then((results) => setLocations(results.flat()))
        .catch(console.error);
    }
  }, [lines]);

  useEffect(() => {
    if (isApprover) load();
    else setLoading(false);
  }, [isApprover, load]);

  const currency = abbreviationFor(currencies, claim?.metricId);
  const money = (value) => formatAmount(value, currency);

  /** Proofs attached to the claim as a whole, and those tied to a trip line. */
  const claimProofs = useMemo(
    () => proofs.filter((proof) => proof.claimLineId == null),
    [proofs]
  );
  const proofsForLine = useCallback(
    (lineId) => proofs.filter((proof) => String(proof.claimLineId) === String(lineId)),
    [proofs]
  );

  /**
   * Whether this viewer may action this claim.
   *
   * The server decides, and states it as `canAct`. The fallback below is the
   * screen-level backstop the PRD asks for while that flag is unavailable: a
   * claim you submitted is never actionable by you, whatever roles you hold,
   * and a claim that has left the chain has nothing left to decide.
   */
  const blockReason = !claim
    ? "loading"
    : !isApprover
      ? "notAnApprover"
      : viewerEmpId != null && Number(claim.empId) === Number(viewerEmpId)
        ? "ownClaim"
        : claim.canAct === false ||
          !(
            [
              CLAIM_STATUS.SUBMITTED,
              // A late claim enters the same chain as any other.
              CLAIM_STATUS.LATE_SUBMITTED,
              CLAIM_STATUS.L1_APPROVED,
            ].includes(claim.claimStatus) ||
            (isFinanceManager && claim.claimStatus === CLAIM_STATUS.APPROVED)
          )
          ? "notAwaitingAction"
          : null;
  const canAct = blockReason === null;

  const handleAction = async (reason) => {
    setActionLoading(true);
    setActionError("");
    try {
      if (openAction === APPROVER_ACTION.MARK_PROCESSED) {
        await conveyanceClaimService.markClaimsProcessed([claimId]);
      } else {
        await submitApproverAction({ claimId, action: openAction, reason });
      }
      setOpenAction(null);
      setToast({ tone: "success", message: t("conveyanceApproval.actionRecorded") });
      load();
    } catch (error) {
      setActionError(
        error.messageKey
          ? t(error.messageKey)
          : error.message || t("conveyanceApproval.errorAction")
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (!isApprover) return <ApproverDeniedScreen requiredRole="Manager / Finance_Manager" />;

  if (loading) {
    return (
      <AppLayout>
        <PageTitleRow className="mb-5" back={<PageBackLink to={backTo} label={t("conveyanceApproval.backToList")} />}><h1>{t("conveyanceApproval.claimSummary")}</h1></PageTitleRow>
        <ContentCard>
          <LoadingState message={t("conveyanceApproval.loadingClaim")} />
        </ContentCard>
      </AppLayout>
    );
  }

  if (loadError || !claim) {
    return (
      <AppLayout>
        <PageTitleRow className="mb-5" back={<PageBackLink to={backTo} label={t("conveyanceApproval.backToList")} />}><h1>{t("conveyanceApproval.claimSummary")}</h1></PageTitleRow>
        <ContentCard bodyClassName="p-10 text-center">
          <p className="text-sm font-bold text-foreground">
            {loadError
              ? t("conveyanceApproval.errorLoadClaim")
              : t("conveyanceApproval.claimNotFoundTitle")}
          </p>
          <p className="mt-1 text-sm per-file-hint">
            {loadError || t("conveyanceApproval.claimNotFoundMessage")}
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-(--color-text-border) text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer"
          >
            <RefreshCw className="h-4 w-4" />
            {t("localConveyance.retry")}
          </button>
        </ContentCard>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageTitleRow className="mb-5" back={<PageBackLink to={backTo} label={t("conveyanceApproval.backToList")} />}>
        <h1>{t("conveyanceApproval.claimSummary")}</h1>
      </PageTitleRow>

      {/* Header block */}
      <ContentCard className="mb-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 px-4 sm:px-6 py-5">
          <HeaderField label={t("conveyanceClaim.claimNo")}>
            {claim.claimNo || `#${claim.claimId}`}
          </HeaderField>

          <HeaderField label={t("conveyanceApproval.submitter")}>
            <span>{claimSubmitterName(claim)}</span>
            {claim.empCode || claim.departmentName ? (
              <span className="mt-0.5 block text-[11px] font-normal per-file-hint">
                {[claim.empCode, claim.departmentName].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </HeaderField>

          <HeaderField label={t("conveyanceClaim.claimCategory")}>
            {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)}
          </HeaderField>

          <HeaderField label={t("conveyanceClaim.periodDate")}>
            {formatClaimPeriod(claim, t)}
          </HeaderField>

          <HeaderField label={t("conveyanceClaim.claimTotal")}>
            <span className="text-base font-extrabold">{money(claim.totalAmount)}</span>
          </HeaderField>

          <HeaderField label={t("conveyanceApproval.currentStatus")}>
            <ClaimStatusBadge status={claim.claimStatus} />
          </HeaderField>

          <HeaderField label={t("conveyanceApproval.submittedOn")}>
            {formatStamp(claim.submittedOn, t) || "—"}
          </HeaderField>

          {lateSubmissionReason(claim) ? (
            <div className="col-span-2 lg:col-span-4">
              <span className={ui.sectionLabel}>{t("conveyanceClaim.lateReason")}</span>
              <p className="mt-1 text-sm text-foreground leading-relaxed whitespace-pre-wrap wrap-break-word">
                {lateSubmissionReason(claim)}
              </p>
            </div>
          ) : null}

          {claimApprovedOn(claim) ? (
            <HeaderField label={t("conveyanceApproval.approvedOn")}>
              {formatStamp(claimApprovedOn(claim), t)}
            </HeaderField>
          ) : null}
        </div>
      </ContentCard>

      {/* Body — one shape per claim category, all read-only renders of what
          the submitter entered. Rate and mode come from the claim as it was
          submitted, never recalculated, so a mid-month policy change cannot
          alter what an approver is looking at. */}
      {claim.category === CLAIM_CATEGORY.RATE_PER_KM ? (
        <ContentCard
          title={t("conveyanceApproval.tripLines", { count: lines.length })}
          className="mb-5"
        >
          {lines.length === 0 ? (
            <p className="px-4 sm:px-6 py-8 text-center text-sm per-file-hint">
              {t("conveyanceApproval.noTripLines")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <ClientTable className="w-full text-sm" style={{ minWidth: "1180px" }}>
                <thead>
                  <tr className={ui.tableHead}>
                    <th className={headCellClass}>{t("conveyanceClaim.tripDate")}</th>
                    <th className={headCellClass}>{t("conveyanceClaim.reason")}</th>
                    <th className={headCellClass}>
                      {t("conveyanceApproval.customerDescription")}
                    </th>
                    <th className={headCellClass}>{t("conveyanceApproval.from")}</th>
                    <th className={headCellClass}>{t("conveyanceApproval.to")}</th>
                    <th className={headCellClass}>{t("conveyanceClaim.area")}</th>
                    <th className={headCellClass}>{t("conveyanceClaim.modeOfTransport")}</th>
                    <th className={cn(headCellClass, "text-right")}>
                      {t("conveyanceClaim.distanceKm")}
                    </th>
                    <th className={cn(headCellClass, "text-right")}>
                      {t("conveyanceApproval.rate")}
                    </th>
                    <th className={cn(headCellClass, "text-right")}>
                      {t("conveyanceClaim.amount")}
                    </th>
                    <th className={cn(headCellClass, "text-right")}>
                      {t("conveyanceClaim.parkingCharges")}
                    </th>
                    <th className={cn(headCellClass, "text-right")}>
                      {t("conveyanceClaim.tollCharges")}
                    </th>
                    <th className={cn(headCellClass, "text-right")}>
                      {t("conveyanceClaim.lineTotal")}
                    </th>
                    <th className={headCellClass}>{t("conveyanceClaim.proofs")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-(--color-text-border)/35">
                  {lines.map((line, index) => {
                    const mappedCustomer = customers.find((c) => String(c.id) === String(line.customerId))?.name;
                    const mappedArea = locations.find((l) => String(l.id) === String(line.areaLocationId))?.name;
                    return (
                    <tr key={line.claimLineId ?? index} className="align-top">
                      <td className={cn(cellClass, "whitespace-nowrap text-foreground font-semibold")}>
                        {formatDate(line.tripDate, t) || "—"}
                      </td>
                      <td className={cellClass}>
                        {line.tripReason ? t(TRIP_REASON_LABEL_KEY[line.tripReason] || line.tripReason) : "—"}
                      </td>
                      <td className={cn(cellClass, "max-w-[220px]")}>
                        {mappedCustomer || line.customerName || line.claimDescription || "—"}
                      </td>
                      <td className={cellClass}>{line.fromLocation || "—"}</td>
                      <td className={cellClass}>{line.toLocation || "—"}</td>
                      <td className={cellClass}>{mappedArea || line.areaName || line.areaText || "—"}</td>
                      <td className={cellClass}>
                        {line.modeTransport ? t(MODE_LABEL_KEY[line.modeTransport] || line.modeTransport) : "—"}
                      </td>
                      <td className={cn(cellClass, "text-right")}>
                        {line.distanceKm != null ? Number(line.distanceKm).toFixed(1) : "—"}
                      </td>
                      {/* The rate snapshotted onto the line at submission. */}
                      <td className={cn(cellClass, "text-right")}>
                        {line.ratePerKm != null ? money(line.ratePerKm) : "—"}
                      </td>
                      <td className={cn(cellClass, "text-right text-foreground")}>
                        {line.amount != null ? money(line.amount) : (line.totalAmount != null ? money(line.totalAmount) : "—")}
                      </td>
                      <td className={cn(cellClass, "text-right")}>
                        {line.parkingCharges != null ? money(line.parkingCharges) : "—"}
                      </td>
                      <td className={cn(cellClass, "text-right")}>
                        {line.tollCharges != null ? money(line.tollCharges) : "—"}
                      </td>
                      <td className={cn(cellClass, "text-right font-bold text-foreground")}>
                        {line.totalAmount != null ? money(line.totalAmount) : "—"}
                      </td>
                      <td className={cellClass}>
                        <ProofCell
                          proofs={proofsForLine(line.claimLineId)}
                          onOpen={setPreviewProof}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </ClientTable>
            </div>
          )}
        </ContentCard>
      ) : null}

      {claim.category === CLAIM_CATEGORY.FIXED_MONTHLY ? (
        <ContentCard title={t("conveyanceClaim.claimDetail")} className="mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 px-4 sm:px-6 py-5">
            <HeaderField label={t("conveyanceClaim.date")}>
              {formatClaimPeriod(claim, t)}
            </HeaderField>
            <HeaderField label={t("conveyanceClaim.amount")}>
              {money(claim.totalAmount)}
            </HeaderField>
            <HeaderField label={t("conveyanceClaim.proofs")}>
              {/* Optional for this category — the cell says so rather than
                  flagging an absence that breaks no rule. */}
              <ProofCell proofs={claimProofs} onOpen={setPreviewProof} />
            </HeaderField>
          </div>
        </ContentCard>
      ) : null}

      {claim.category === CLAIM_CATEGORY.MAINTENANCE ? (
        <ContentCard title={t("conveyanceClaim.claimDetail")} className="mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 px-4 sm:px-6 py-5">
            <HeaderField label={t("conveyanceClaim.date")}>
              {formatDate(claim.claimDate, t) || "—"}
            </HeaderField>
            <HeaderField label={t("conveyanceClaim.vehicleType")}>
              {claim.vehicleType
                ? t(VEHICLE_TYPE_LABEL_KEY[claim.vehicleType] || claim.vehicleType)
                : "—"}
            </HeaderField>
            <HeaderField label={t("conveyanceClaim.amount")}>
              {money(claim.totalAmount)}
            </HeaderField>
            <div className="sm:col-span-3">
              <span className={ui.sectionLabel}>{t("conveyanceClaim.proofs")}</span>
              <div className="mt-1.5">
                <ProofCell proofs={claimProofs} required onOpen={setPreviewProof} />
              </div>
            </div>
          </div>
        </ContentCard>
      ) : null}

      {/* Action bar — exactly three actions, and nothing else. When the viewer
          cannot act, the reason replaces the buttons instead of leaving an
          unexplained gap. It leads the page: an approver opens this screen to
          decide, and reads the history below only to inform that decision. */}
      <ContentCard className="mb-5">
        {canAct ? (
          <div className="flex flex-col gap-3 px-4 sm:px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[12px] per-file-hint">
              {t("conveyanceApproval.noEditNote")}
            </p>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {claim.claimStatus !== CLAIM_STATUS.APPROVED ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setActionError("");
                      setOpenAction(APPROVER_ACTION.SEND_BACK);
                    }}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-amber-300 bg-white text-sm font-semibold text-amber-700 hover:bg-amber-50 transition cursor-pointer"
                  >
                    <Undo2 className="h-4 w-4" />
                    {t("conveyanceApproval.sendBack")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionError("");
                      setOpenAction(APPROVER_ACTION.REJECT);
                    }}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-rose-300 bg-white text-sm font-semibold text-rose-700 hover:bg-rose-50 transition cursor-pointer"
                  >
                    <Ban className="h-4 w-4" />
                    {t("conveyanceApproval.reject")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionError("");
                      setOpenAction(APPROVER_ACTION.APPROVE);
                    }}
                    className={cn(ui.primaryBtn, "h-10 px-5")}
                  >
                    <Check className="h-4 w-4" />
                    {t("conveyanceApproval.approve")}
                  </button>
                </>
              ) : isFinanceManager ? (
                <button
                  type="button"
                  onClick={() => {
                    setActionError("");
                    setOpenAction(APPROVER_ACTION.MARK_PROCESSED);
                  }}
                  className={cn(ui.primaryBtn, "h-10 px-5")}
                >
                  <Check className="h-4 w-4" />
                  {t("conveyanceApproval.markProcessed")}
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 px-4 sm:px-6 py-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 per-file-hint" />
            <p className="text-[13px] per-file-hint leading-relaxed">
              {t(`conveyanceApproval.blocked_${blockReason}`)}
            </p>
          </div>
        )}
      </ContentCard>

      {/* Approval history */}
      <ContentCard title={t("conveyanceApproval.auditTrail")}>
        <ClaimAuditTrail actions={actions} />
      </ContentCard>

      <ApproverActionModal
        action={openAction}
        open={Boolean(openAction)}
        onOpenChange={(open) => {
          if (!open) setOpenAction(null);
        }}
        onConfirm={handleAction}
        loading={actionLoading}
        error={actionError}
        summary={
          <div className="space-y-0.5">
            <p className="font-semibold text-foreground">
              {claim.claimNo || `#${claim.claimId}`} · {claim.empName}
            </p>
            <p className="per-file-hint">
              {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)} ·{" "}
              {formatClaimPeriod(claim, t)} · {money(claim.totalAmount)}
            </p>
          </div>
        }
      />

      <ProofPreviewPanel proof={previewProof} onClose={() => setPreviewProof(null)} />

      {toast ? (
        <Toast tone={toast.tone} onDone={() => setToast(null)}>
          {toast.message}
        </Toast>
      ) : null}
    </AppLayout>
  );
}

export default ClaimApprovalDetailPage;
